/**
 * Servizio per autenticazione con Partita IVA e OTP
 */

import axios from 'axios';
import { esitoSessioneDaStatus, EsitoSessione, codiceDaRisposta } from '../utils/sessioneAssociato';
import { gestisciRispostaDipendente, segnaStudioInRegola, azzeraStatoDipendente, autorizzazioneRichiesta } from './fineSessioneDipendente';
import { leggiPayload, statoToken } from '../utils/rinnovoSessione';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

/**
 * Dipendente dello studio (Ruling R15/R16): ogni risposta dell'area riservata passa da qui.
 * 401 `DIPENDENTI_SPENTI`/`SESSIONE_NON_VALIDA` chiudono la sessione, 403 `STUDIO_NON_IN_REGOLA`
 * chiude le sezioni (vedi fineSessioneDipendente). Per il titolare non succede nulla: risposte ed
 * errori proseguono identici. Anche le risposte lasciate passare con `validateStatus` permissivo.
 */
try {
  axios.interceptors.response.use(
    (response) => {
      if (response && typeof response.status === 'number' && response.status >= 400) {
        gestisciRispostaDipendente(response.status, response.data, autorizzazioneRichiesta(response.config));
      }
      return response;
    },
    (error) => {
      gestisciRispostaDipendente(error?.response?.status, error?.response?.data, autorizzazioneRichiesta(error?.config ?? error?.response?.config));
      return Promise.reject(error);
    }
  );
} catch (e) {
  // axios simulato (test) senza intercettori: nessun effetto
}

export interface SendOTPRequest {
  partitaIVA: string;
  associateId?: string; // Per selezione sede specifica
}

export interface LocationOption {
  id: string;
  codice: string;
  nominativo: string;
  indirizzo: string;
  email: string; // mascherata
  categoria?: string;
}

export interface SendOTPResponse {
  success: boolean;
  message: string;
  multipleLocations?: boolean; // true se ci sono più sedi
  locations?: LocationOption[]; // Lista sedi per selezione
  data?: {
    email: string; // Email mascherata
    expiresIn: number; // Minuti
    associateId?: string; // ID sede selezionata
  };
}

export interface VerifyOTPRequest {
  partitaIVA: string;
  otpCode: string;
}

export interface RelatedSede {
  id: string;
  codice: string;
  nominativo: string;
  indirizzo: string;
  stato: string;
}

export interface AssociateData {
  id: string;
  codice: string;
  nominativo: string;
  partitaIVA: string;
  codiceFiscale?: string;
  rappresentante?: string;
  rappresentanteNome?: string;
  rappresentanteCognome?: string;
  rapprLegale?: string;
  rapprLegaleNome?: string;
  rapprLegaleCognome?: string;
  email: string;
  telefono?: string;
  regione: string;
  provincia: string;
  citta: string;
  indirizzo?: string;
  civico?: string;
  cap?: string;
  stato: string;
  categoria?: string;
  integrativa?: string;
  totIntegrativaSede?: string;
  iscrizione?: string;
  statoTesseramento: string;
  tipoPagamento?: string;
  isInCertifiedNetwork: boolean;
  // Account dimostrativo Facile.it: nell'area riservata vede solo la card Facile
  demoFacile?: boolean;
  // Campi profilo arricchito (compilati dall'associato)
  pec?: string;
  emailSecondarie?: string[];
  telefoniExtra?: string[];
  foto?: string;
  attivitaSvolte?: Array<{ _id: string; nome: string; categoria: string }>;
  coordinates?: {
    lat?: number;
    lng?: number;
    geocoded?: boolean;
    geocodedAt?: string;
    geocodedAddress?: string;
    geocodeQuality?: 'none' | 'city' | 'street' | 'exact';
  };
  relatedSedi?: RelatedSede[];
  // Stato dell'accesso con password (da GET /auth/me); mai l'hash
  auth?: {
    hasPassword: boolean;
    temporary: boolean;
    passwordSetAt: string | null;
    lastLoginAt: string | null;
    lastLoginMethod: 'otp' | 'password' | 'bridge' | null;
  };
  // Chi è entrato: il titolare (token dello studio) o un dipendente dello studio.
  // I backend precedenti ai dipendenti non mandano `ruolo`: assente = titolare.
  ruolo?: 'titolare' | 'dipendente';
  dipendente?: DipendenteSessione;
}

/** Il dipendente entrato con il suo accesso (`/auth/dipendente/login`, `/auth/me`). */
export interface DipendenteSessione {
  id: string;
  nome: string;
  cognome: string;
  sezioni: string[];
}

export interface CertificateYear {
  year: number;
  certificateStatus: 'ATTIVO' | 'NON ATTIVO' | 'SCADUTO' | 'IN ATTESA';
  paymentStatus: 'PAGATO' | 'NON PAGATO' | 'IN ATTESA';
  paymentDate: string | null;
  amount: number;
  receiptNumber: string | null;
  receiptId: string | null;
  // Bonifico bancario
  paymentMethod?: 'CARTA_CREDITO' | 'BONIFICO' | null;
  bankTransferType?: 'ISTANTANEO' | 'NORMALE' | null;
  bankTransferUploadedAt?: string | null;
}

export interface CertifiedActivity {
  name: string;
  isCertified: 'SI' | 'NO' | 'PREAUTORIZZATO';
}

export interface CertifiedNetworkStatus {
  isMember: boolean;
  isActive: boolean;
  hasApplication: boolean;
  applicationStatus: string | null;
  canApply: boolean;
  message: string;
  certificateHistory: CertificateYear[];
  memberId: string | null;
  currentCertificateStatus: 'ATTIVO' | 'NON ATTIVO' | 'SCADUTO' | 'IN ATTESA' | null;
  certifiedActivities: CertifiedActivity[];
  certificateNumber: string | null;
  certificateScannedPdf: string | null;
  welcomeVideo?: { url: string; title: string } | null;
}

export interface CertifiedNetworkStatusResponse {
  success: boolean;
  data: CertifiedNetworkStatus;
}

export interface VerifyOTPResponse {
  success: boolean;
  message: string;
  data?: {
    token: string;
    associate: AssociateData;
  };
  attemptsRemaining?: number;
}

export interface RefreshTokenRequest {
  token: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  data?: {
    token: string;
  };
}

/**
 * Invia codice OTP via email
 * Se esistono più sedi, restituisce la lista per selezione (multipleLocations: true)
 */
export const sendOTP = async (partitaIVA: string, associateId?: string): Promise<SendOTPResponse> => {
  try {
    const response = await axios.post<SendOTPResponse>(
      `${API_BASE_URL}/auth/send-otp`,
      { partitaIVA, associateId }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Errore durante l\'invio del codice OTP');
    }
    throw new Error('Errore di connessione al server');
  }
};

/**
 * Verifica codice OTP e ottiene token JWT
 * Supporta sia partitaIVA che associateId per gestione multi-sede
 */
export const verifyOTP = async (
  partitaIVA: string,
  otpCode: string,
  associateId?: string,
  representativeId?: string
): Promise<VerifyOTPResponse> => {
  try {
    const body: any = { partitaIVA, otpCode };
    if (associateId) body.associateId = associateId;
    if (representativeId) body.representativeId = representativeId;

    const response = await axios.post<VerifyOTPResponse>(
      `${API_BASE_URL}/auth/verify-otp`,
      body
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    throw new Error('Errore di connessione al server');
  }
};

/**
 * Scambia il codice monouso generato dall'app mobile con una sessione associato standard.
 * Usato dalla pagina di login quando arriva ?bridge=<code>.
 */
export const exchangeBridgeCode = async (code: string): Promise<VerifyOTPResponse> => {
  try {
    const response = await axios.post<VerifyOTPResponse>(
      `${API_BASE_URL}/auth/bridge-exchange`,
      { code }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    throw new Error('Errore di connessione al server');
  }
};

export interface RepresentativeBridgeResponse {
  success: boolean;
  message?: string;
  data?: {
    token: string;
    tipo: 'representative';
    representative: { id: string; nome: string; cognome: string; tipo: string };
  };
}

/**
 * Ponte app -> sito del rappresentante: scambia il codice monouso dell'app
 * (POST /api/auth/rep-mobile/bridge) con il representative_token del sito.
 * Usato dalla pagina di login quando arriva ?bridge=<code>&tipo=rappresentante.
 */
export const exchangeRepresentativeBridgeCode = async (code: string): Promise<RepresentativeBridgeResponse> => {
  try {
    const response = await axios.post<RepresentativeBridgeResponse>(
      `${API_BASE_URL}/auth/rep-mobile/bridge/exchange`,
      { code }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    throw new Error('Errore di connessione al server');
  }
};

export interface PasswordLoginResponse extends VerifyOTPResponse {
  data?: VerifyOTPResponse['data'] & { mustChangePassword?: boolean };
}

/**
 * Accesso con identificativo (P.IVA o email) + password, alternativa all'OTP.
 * 401 (credenziali) e 429 (blocco tentativi) tornano come body, non come eccezione.
 */
export const loginWithPassword = async (identifier: string, password: string): Promise<PasswordLoginResponse> => {
  try {
    const response = await axios.post<PasswordLoginResponse>(
      `${API_BASE_URL}/auth/login-password`,
      { identifier: identifier.trim(), password }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    throw new Error('Errore di connessione al server');
  }
};

export interface PasswordChangeResponse {
  success: boolean;
  message?: string;
  reason?: 'too_short' | 'too_obvious' | 'common' | 'uguale_titolare';
  data?: { hasPassword: boolean; passwordSetAt?: string; token?: string };
}

/**
 * Cambiare o togliere la password chiude le sessioni del sito (il token usato non vale più): il
 * server manda il token nuovo, che si salva al posto di quello usato. Se nel frattempo il token
 * salvato è cambiato (logout, accesso di un'altra persona) non si tocca nulla.
 */
const salvaTokenDopoPassword = (usato: string | null, risposta: PasswordChangeResponse | undefined): void => {
  const nuovo = risposta?.data?.token;
  if (!risposta?.success || typeof nuovo !== 'string' || !nuovo) return;
  if (getToken() !== usato) return;
  saveToken(nuovo);
};

/** Imposta o cambia la password dell'associato autenticato (currentPassword solo se ne ha gia' una non temporanea). */
export const setAssociatePassword = async (
  body: { currentPassword?: string; newPassword: string }
): Promise<PasswordChangeResponse> => {
  try {
    const usato = getToken();
    const response = await axios.put<PasswordChangeResponse>(
      `${API_BASE_URL}/auth/password`,
      body,
      { headers: getAuthHeader() as any }
    );
    salvaTokenDopoPassword(usato, response.data);
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    throw new Error('Errore di connessione al server');
  }
};

/** Rimuove la password: si torna al solo OTP. */
export const removeAssociatePassword = async (currentPassword: string): Promise<PasswordChangeResponse> => {
  try {
    const usato = getToken();
    const response = await axios.delete<PasswordChangeResponse>(
      `${API_BASE_URL}/auth/password`,
      { headers: getAuthHeader() as any, data: { currentPassword } }
    );
    salvaTokenDopoPassword(usato, response.data);
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    throw new Error('Errore di connessione al server');
  }
};

export interface DipendenteLoginResponse {
  success: boolean;
  message?: string;
  code?: string;
  data?: {
    token: string;
    associate: AssociateData;
    dipendente: DipendenteSessione;
    ruolo: 'dipendente';
  };
}

/**
 * Accesso del dipendente dello studio: P.IVA dello studio + nome e cognome + password.
 * 401 (credenziali), 429 (blocco tentativi) e 403 (`STUDIO_NON_IN_REGOLA`) tornano come body,
 * con il messaggio del server, non come eccezione.
 */
export const loginDipendente = async (
  partitaIVA: string,
  nominativo: string,
  password: string
): Promise<DipendenteLoginResponse> => {
  try {
    const response = await axios.post<DipendenteLoginResponse>(
      `${API_BASE_URL}/auth/dipendente/login`,
      { partitaIVA: partitaIVA.trim(), nominativo: nominativo.trim(), password }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    throw new Error('Errore di connessione al server');
  }
};

/**
 * Dati da salvare in `associate_data` dopo il login del dipendente: lo studio con dentro
 * `ruolo` e `dipendente`, così sezioni, intestazione e menu sanno chi è entrato.
 */
export const datiSessioneDipendente = (data: NonNullable<DipendenteLoginResponse['data']>): AssociateData => ({
  ...data.associate,
  ruolo: 'dipendente',
  dipendente: data.dipendente,
});

/**
 * Il dipendente cambia la propria password (serve quella attuale). Il server risponde con un
 * token NUOVO (quello di prima non vale più): qui si salva subito al posto del vecchio.
 * 401 (attuale sbagliata) e 400 (`reason`, password debole) tornano come body.
 */
export const changeDipendentePassword = async (attuale: string, nuova: string): Promise<PasswordChangeResponse & { data?: { token?: string } }> => {
  try {
    const response = await axios.put<PasswordChangeResponse & { data?: { token?: string } }>(
      `${API_BASE_URL}/auth/dipendente/password`,
      { attuale, nuova },
      { headers: getAuthHeader() as any }
    );
    if (response.data?.success && response.data.data?.token) {
      saveToken(response.data.data.token);
    }
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    throw new Error('Errore di connessione al server');
  }
};

/**
 * Rinnova token JWT. Il rinnovo automatico del sito è in services/rinnovoSessioneAssociato.ts.
 */
export const refreshToken = async (token: string): Promise<RefreshTokenResponse> => {
  try {
    const response = await axios.post<RefreshTokenResponse>(
      `${API_BASE_URL}/auth/refresh-token`,
      { token }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Errore durante il rinnovo del token');
    }
    throw new Error('Errore di connessione al server');
  }
};

/**
 * Salva token nel localStorage
 */
export const saveToken = (token: string): void => {
  localStorage.setItem('associate_token', token);
};

/**
 * Ottiene token dal localStorage
 */
export const getToken = (): string | null => {
  return localStorage.getItem('associate_token');
};

/**
 * Rimuove token dal localStorage
 */
export const removeToken = (): void => {
  localStorage.removeItem('associate_token');
};

/**
 * Salva dati associato nel localStorage
 */
export const saveAssociateData = (associate: AssociateData): void => {
  localStorage.setItem('associate_data', JSON.stringify(associate));
};

/**
 * Ottiene dati associato dal localStorage
 */
export const getAssociateData = (): AssociateData | null => {
  const data = localStorage.getItem('associate_data');
  return data ? JSON.parse(data) : null;
};

/**
 * Rimuove dati associato dal localStorage
 */
export const removeAssociateData = (): void => {
  localStorage.removeItem('associate_data');
};

/** Funzioni da chiamare al logout dell'associato (es. album della chat in invio da dimenticare). */
const suLogout = new Set<() => void>();

/**
 * Registra una funzione da chiamare a ogni `logout()` dell'associato; restituisce la
 * funzione che la toglie. Un errore di una funzione non ferma il logout né le altre.
 */
export const alLogoutAssociato = (fn: () => void): (() => void) => {
  suLogout.add(fn);
  return () => {
    suLogout.delete(fn);
  };
};

/**
 * Sospensione del logout ancora in corso (null se nessuna). Il ricollegamento della persona che entra
 * (`ricollegaIscrizione` in hooks/usePushNotifications.ts) la aspetta: la sospensione vale per tutto lo
 * studio, e se arrivasse al server dopo il subscribe del dipendente gli toglierebbe l'iscrizione appena presa.
 */
let sospensioneLogout: Promise<unknown> | null = null;

/** Promessa della sospensione del logout ancora in corso, o null. Non viene mai rifiutata. */
export const sospensionePushInCorso = (): Promise<unknown> | null => sospensioneLogout;

/**
 * Sospende sul server l'iscrizione push di questo browser (POST /push/sospendi): resta nel browser e si
 * ricollega al prossimo login. Fire-and-forget con keepalive, errori ignorati. Il token va letto prima
 * di toglierlo, perché la richiesta parte dopo (l'endpoint si legge in modo asincrono).
 */
const sospendiPushBrowser = (token: string | null): void => {
  try {
    if (!token || typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistration) return;
    const sospensione: Promise<unknown> = navigator.serviceWorker.getRegistration()
      .then((registration) => registration?.pushManager?.getSubscription())
      .then((subscription) => {
        if (!subscription?.endpoint) return;
        return fetch(`${API_BASE_URL}/push/sospendi`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
      })
      .catch(() => {
        // il logout resta fatto anche se la sospensione non arriva al server
      })
      .finally(() => {
        if (sospensioneLogout === sospensione) sospensioneLogout = null;
      });
    sospensioneLogout = sospensione;
  } catch (e) {
    // idem
  }
};

/**
 * PC condiviso: chiude le notifiche della chat già mostrate da questo browser (`data.category`
 * `community_chat`), così chi usa il PC dopo il logout non legge anteprime dei messaggi. Best-effort:
 * ogni errore è ignorato e il logout resta fatto.
 */
const chiudiNotificheChatBrowser = (): void => {
  try {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistration) return;
    navigator.serviceWorker.getRegistration()
      .then((registration) => (registration && typeof registration.getNotifications === 'function' ? registration.getNotifications() : []))
      .then((notifiche) => {
        (notifiche || []).forEach((n) => {
          try {
            if (n && n.data && n.data.category === 'community_chat') n.close();
          } catch (e) {
            // una notifica che non si chiude non ferma le altre
          }
        });
      })
      .catch(() => {
        // notifiche non raggiungibili: restano, il logout è fatto comunque
      });
  } catch (e) {
    // idem
  }
};

/**
 * Logout completo
 */
export const logout = (): void => {
  sospendiPushBrowser(getToken());
  chiudiNotificheChatBrowser();
  removeToken();
  removeAssociateData();
  azzeraStatoDipendente();
  Array.from(suLogout).forEach((fn) => {
    try {
      fn();
    } catch (e) {
      // il logout resta fatto anche se una pulizia non riesce
    }
  });
};

/**
 * Verifica se l'utente è autenticato (lettura locale del token: presenza e scadenza).
 * «Rinnovo a 90» (25/10/2026): un token scaduto ma ancora rinnovabile dal server (sito, entro
 * 90 giorni dall'emissione, mai impersonificazione) NON si butta: vale «non autenticato» finché
 * services/rinnovoSessioneAssociato.ts non lo rinnova (all'avvio, prima del primo disegno).
 * Scaduto e non rinnovabile, o malformato ⇒ logout come prima.
 */
export const isAuthenticated = (): boolean => {
  const token = getToken();
  if (!token) return false;

  const payload = leggiPayload(token);
  if (!payload) {
    // Token malformato, rimuovi dati
    logout();
    return false;
  }
  const stato = statoToken(payload);
  if (stato === 'valido') return true;
  if (stato === 'rinnovabile') return false;
  // Token scaduto e non più rinnovabile, rimuovi dati
  logout();
  return false;
};

/**
 * Ottiene header Authorization per richieste API
 */
export const getAuthHeader = (): { Authorization: string } | {} => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Chiede al SERVER se il token in localStorage vale ancora.
 *
 * `isAuthenticated()` legge solo il payload del token (presenza + scadenza): non puo'
 * accorgersi di una firma non piu' valida. E' successo il 29/08/2026 con la rotazione di
 * JWT_SECRET: il sito sembrava loggato, ogni API rispondeva 401 e l'estensione Takata
 * (che riceve lo stesso token dal sito) mandava «Accedi all'area riservata» a chi c'era
 * gia' dentro. Si usciva solo con logout + login. Questa verifica, fatta all'ingresso
 * nell'area riservata (vedi AssociatePrivateRoute), chiude quel vicolo cieco.
 *
 * Non lancia mai: rete assente o server in affanno → 'indeterminata' (nessun logout).
 * `validateStatus` accetta ogni status per leggere il codice invece di finire nel catch.
 */
export const verificaSessioneServer = async (): Promise<EsitoSessione> => {
  if (!getToken()) return 'rifiutata';
  try {
    const intestazioni = getAuthHeader() as { Authorization?: string };
    const response = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: intestazioni as any,
      validateStatus: () => true,
    });
    // Dipendente: fine sessione (401 coi suoi codici) o studio non in regola (403, la sessione resta)
    gestisciRispostaDipendente(response.status, response.data, intestazioni.Authorization ?? null);
    const esito = esitoSessioneDaStatus(response.status, codiceDaRisposta(response.data));
    if (esito === 'valida') {
      segnaStudioInRegola();
      allineaSessioneDipendente(response.data?.data);
    }
    return esito;
  } catch (error) {
    return 'indeterminata';
  }
};

/**
 * Dipendente dello studio: riporta `ruolo` e `dipendente` di `/auth/me` in `associate_data`, così
 * una sezione tolta dal titolare sparisce da route, intestazione e menu senza passare dalla
 * dashboard, e il ponte app → sito (dati salvati come studio) prende il ruolo giusto.
 * Per il titolare (`/auth/me` senza `ruolo: 'dipendente'` né `dipendente`) non si scrive nulla.
 * Non lancia mai: un errore qui non deve cambiare l'esito della verifica.
 */
const allineaSessioneDipendente = (dati: AssociateData | undefined | null): void => {
  try {
    if (!dati || (dati.ruolo !== 'dipendente' && !dati.dipendente)) return;
    let salvati: AssociateData | null = null;
    try {
      salvati = getAssociateData();
    } catch (e) {
      salvati = null;
    }
    const allineati: AssociateData = { ...(salvati || dati), ruolo: 'dipendente', dipendente: dati.dipendente };
    if (salvati && JSON.stringify(salvati) === JSON.stringify(allineati)) return;
    saveAssociateData(allineati);
  } catch (e) {
    // i dati salvati restano quelli di prima
  }
};

/**
 * Ottiene i dati aggiornati dell'associato autenticato
 */
export const refreshAssociateData = async (): Promise<AssociateData> => {
  const response = await axios.get<{ success: boolean; data: AssociateData }>(
    `${API_BASE_URL}/auth/me`,
    { headers: getAuthHeader() as any }
  );
  segnaStudioInRegola();

  // Aggiorna i dati nel localStorage
  const currentData = localStorage.getItem('associate_auth');
  if (currentData) {
    const parsed = JSON.parse(currentData);
    parsed.associate = response.data.data;
    localStorage.setItem('associate_auth', JSON.stringify(parsed));
  }

  // Dipendente: le sezioni abilitate possono essere cambiate dal titolare; si aggiornano anche
  // in `associate_data` (route, intestazione e menu leggono da lì). Il titolare resta com'è oggi.
  if (response.data.data?.ruolo === 'dipendente') {
    saveAssociateData(response.data.data);
  }

  return response.data.data;
};

const ASSOCIATE_API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

export interface AssociateProfileUpdate {
  pec?: string;
  emailSecondarie?: string[];
  telefoniExtra?: string[];
  attivitaSvolte?: string[];
  rcWelcomeVideoDismissedUrl?: string;
}

export interface AssociateProfileData {
  pec?: string;
  emailSecondarie?: string[];
  telefoniExtra?: string[];
  foto?: string;
  attivitaSvolte?: Array<{ _id: string; nome: string; categoria: string }>;
  rcWelcomeVideoDismissedUrl?: string;
}

/**
 * Legge il profilo arricchito dell'associato dall'area riservata
 */
export const getAssociateProfile = async (): Promise<AssociateProfileData> => {
  const response = await axios.get(
    `${ASSOCIATE_API_BASE}/associate/profile`,
    { headers: getAuthHeader() as any }
  );
  return response.data.profile;
};

/**
 * Aggiorna pec, emailSecondarie, telefoniExtra, attivitaSvolte
 */
export const updateAssociateProfile = async (data: AssociateProfileUpdate): Promise<AssociateProfileData> => {
  const response = await axios.put(
    `${ASSOCIATE_API_BASE}/associate/profile`,
    data,
    { headers: getAuthHeader() as any }
  );
  return response.data.profile;
};

/**
 * Carica la foto profilo dell'associato
 */
export const uploadAssociateFoto = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('foto', file);
  const response = await axios.post(
    `${ASSOCIATE_API_BASE}/associate/profile/foto`,
    formData,
    { headers: { ...(getAuthHeader() as any), 'Content-Type': 'multipart/form-data' } }
  );
  return response.data.fotoUrl;
};

/** Dati aggiornati dell'associato (`GET /auth/me`) senza toccare il localStorage. */
export const getMe = async (): Promise<AssociateData> => {
  const response = await axios.get<{ success: boolean; data: AssociateData }>(`${API_BASE_URL}/auth/me`, {
    headers: getAuthHeader() as any,
  });
  return response.data.data;
};

/**
 * Sedi dello studio (`GET /auth/my-sedi`): `data` grezzo `{ sedi, isInCertifiedNetwork }`, con
 * `externalSedeCode` sulle secondarie. Agli studi fuori dalla Rete Certificata `sedi: []`.
 */
export const getMieSedi = async (): Promise<unknown> => {
  const response = await axios.get(`${API_BASE_URL}/auth/my-sedi`, { headers: getAuthHeader() as any });
  return response.data && response.data.data;
};

/** Codici STA e matricole dello studio (`GET /auth/sta-codes`): `data` grezzo, come la card dei codici STA. */
export const getCodiciSta = async (): Promise<unknown> => {
  const response = await axios.get(`${API_BASE_URL}/auth/sta-codes`, { headers: getAuthHeader() as any });
  return response.data && response.data.data;
};

/**
 * Ottiene lo stato della rete certificata per l'associato autenticato
 */
export const getCertifiedNetworkStatus = async (): Promise<CertifiedNetworkStatus> => {
  const response = await axios.get<CertifiedNetworkStatusResponse>(
    `${API_BASE_URL}/auth/certified-network-status`,
    { headers: getAuthHeader() as any }
  );
  return response.data.data;
};

